import { gql } from "graphql-tag"

import { DEFAULTS } from "@/graphql/schemas/shared/utils/defaults"

const typeArticles = gql`

  type ArticleMainGroup {
    id: ID!
    key: String
    name: String
  }

  type ArticleGroup {
    id: ID!
    key: String
    name: String
  }

  type SupplierNote {
    number: Int
    text: String
  }

  type Supplier {
    id: ID!
    name: String
    name2: String
    name3: String
    supplierNr: String
    notes: [SupplierNote]
  }

  type PDFAttachment {
    label: String
    file: JSONObject
  }

  type Bundle {
    type: String
    id: ID!
    image: JSONObject
    descriptionBlocks: JSON
    attachments: [PDFAttachment]
    variants: [Article]
    title: String
    altTitle: String
  }

  type Bundles {
    total: Int
    items: [Bundle]
  }

  type ArticleNotice {
    sku: String
    statusName: String
    description: String
    variant: String
  }

  type Article {
    type: String
    id: ID!
    sku: ID
    sdbSKU: ID

    image: JSONObject
    articleMainGroup: ArticleMainGroup
    articleGroup: ArticleGroup
    supplier: Supplier

    title: String
    altTitle: String

    ean: String
    altEAN: String

    amount: Float
    contents: String
    container: String
    status: String
    color: String
    baseColor: String
    brand: String

    mix: Boolean
    deleted: Boolean
    sale: Boolean
    hidden: Boolean

    price: ListPrice
    attributes: [JSONObject]
    productRef: ProductRef
    notAvailableInBranchOffices: [BranchOffice]
    articleInfo: String
    customerPrice: CustomerPrice!
    availability: String
    packageUnitPerCarton: String
    packageUnitPerPallet: String
    containerPerPallet: Float
    amountBilling: String
    sellableInDecimals: Boolean
    transportWeightGross: Float
    searchTermThree: String
    searchTermFour: String
    artStatus: String
  }

  type TopAdmArticle {
    year: Int
    admNumber: String
    article: Article
    revenue: Float
    quantity: Float
    margin: Float
    amount: Float
  }

  type TopAdmArticles {
    totalPages: Int
    total: Int
    page: Int
    size: Int
    items: [TopAdmArticle]
  }

  type TopCustomerArticle {
    year: Int
    customerNumber: String
    article: Article
    revenue: Float
    quantity: Float
    margin: Float
    amount: Float
  }

  type TopCustomerArticles {
    totalPages: Int
    total: Int
    page: Int
    size: Int
    items: [TopCustomerArticle]
  }

  type Articles {
    limit: Int
    offset: Int
    count: Int
    total: Int
    items: [Article]
  }

  type FacetValues {
    option: String
    amount: Int
  }

  type Facets {
    key: String
    values: [FacetValues]
  }

  type ArticleQuerySuggestions {
    suggestions: [String]
  }

  union SearchItem = Article | Bundle

  type SearchArticleResults {
    limit: Int
    offset: Int
    count: Int
    total: Int
    facets: [Facets]
    totalPages: Int

    items: [SearchItem]
  }

  type ArticleSuggestion {
    sku: String
    title: String
    altTitle: String
    amountContainerContents: String
  }

  input Search {
    searchString: String
    searchInFields: IncludedFields
  }

  input IncludedFields {
    sku: Boolean
    productKey: Boolean
    assortment: Boolean
    ean: Boolean
    title: Boolean
    altTitle: Boolean
    articleInfo: Boolean
    brand: Boolean
    productTitle: Boolean
    titleSearchTerm: Boolean
    searchTermThree: Boolean
    searchTermFour: Boolean
  }

  input FacetInputValue {
    value: String
    label: String
  }

  input FacetsInput {
    key: String
    value: [FacetInputValue]
  }

  enum AVAILABILITY {
    IN_STOCK
    OUT_OF_STOCK
  }

  type Query {
    bundles(
      offset: Int = ${DEFAULTS.QUERY.OFFSET}
      limit: Int = ${DEFAULTS.QUERY.LIMIT}
    ): Bundles

    bundle(
      id: ID!
      loggedInBranch: String
      customerId: ID = null
    ): Bundle

    bundleImage: JSONObject

    article(
      sku: ID!
      customerId: ID = null
      customerGroup: ID = "customer-group-b"
      lang: String = ""
      cdn: Boolean = true
      loggedInBranch: String
    ): Article

    articles(
      offset: Int = ${DEFAULTS.QUERY.OFFSET}
      limit: Int = ${DEFAULTS.QUERY.LIMIT}
      sort: Sort = {
                orderBy: "title",
                direction: "desc"
            }
      sortWithinLimits: Boolean = false
      customerNumber: String
      lang: String = ""
      cdn: Boolean = true
      loggedInBranch: String
      search: Search
      articleMainGroup: ID
      articleGroup: ID
      categories: [ID]
      status: String
      brand: String
      supplier: ID
    ): Articles

    articlesSearch(
      search: Search
      loggedInBranch: String
      cdn: Boolean = true
      lang: String = ""
      customerNumber: String
      offset: Int = ${DEFAULTS.QUERY.OFFSET}
      limit: Int = ${DEFAULTS.QUERY.LIMIT}
      selectedFacets: [FacetsInput]
      page: Int = ${DEFAULTS.QUERY.PAGE}
      onlyInStock: Boolean = false
    ): SearchArticleResults

    suggestedAlgoliaArticles(
      searchTerm: String
      branchId: String
    ): [ArticleSuggestion]

    admTopArticles(
      year: Int!
      admNumber: String!
      page: Int = 0
      size: Int = 5
      sort: Sort = {
                orderBy: "revenue",
                direction: "desc"
            }
      lang: String = ""
      cdn: Boolean = true
    ): TopAdmArticles

    customerTopArticles(
      year: Int!
      customerNumber: String!
      page: Int = 0
      size: Int = 5
      sort: Sort  = {
                orderBy: "revenue",
                direction: "desc"
            }
      lang: String = ""
      cdn: Boolean = true
    ): TopCustomerArticles

    articleQuerySuggestions(searchTerm: String): ArticleQuerySuggestions

    articleNotices(sku: String): [ArticleNotice]
  }
`

export { typeArticles }

export default typeArticles
